import * as yup from 'yup';
import { trimString } from '../../../../utils/trimString';
import { ELECTION_TYPE } from '../../../../utils/constants';
import {
  phdAwardedYearMaxValue,
  pdfFileInputMessage,
  minimumYear,
  descriptorErrorMessage,
  interdisciplinaryErrorMessage,
  defaultMessage,
  notAPdfFile,
  validatePdfType,
  validatePdfSize,
  validateFileRequired,
  validateExpiredNomination,
  validateAlreadyElected,
  validateDeceasedCandidate,
  validateActiveNominationDuplicate,
  validateDraftDuplicate,
  conditionalNameValidation,
  conditionalNameValidationWithElectionType,
} from './validationHelpers';

/**
 * Returns an object-shape fragment with the yup rules for one referee block.
 * Covers referees One through Four, which share the same validation logic.
 * Referees Five and Six have additional electionTypeId-dependent gating and
 * stay defined inline.
 */
const buildRefereeFields = (suffix) => {
  const refereeKey = `referee${suffix}`;
  const fellowIdKey = `referee${suffix}FellowId`;
  const nonAasKey = `nonAasReferee${suffix}Id`;
  const titleIdKey = `referee${suffix}TitleId`;
  const nameKey = `referee${suffix}Name`;
  const lastNameKey = `referee${suffix}LastName`;
  const emailKey = `referee${suffix}Email`;
  const countryIdKey = `referee${suffix}CountryId`;
  const orgKey = `referee${suffix}Organisation`;
  const emailEduKey = `referee${suffix}EmailEducationalInstitution`;
  const membershipsKey = `referee${suffix}Memberships`;
  const suppliedMembershipsKey = `referee${suffix}SuppliedMemberships`;

  return {
    [refereeKey]: yup.number().required().typeError(defaultMessage),
    [fellowIdKey]: yup.object().when(refereeKey, {
      is: (val) => val && typeof val === 'number' && val === 1,
      then: yup.object().required().nullable(),
      otherwise: yup.object().notRequired().nullable(),
    }),
    [nonAasKey]: yup.object().when(refereeKey, {
      is: (val) =>
        val !== null &&
        typeof val !== 'undefined' &&
        typeof val === 'number' &&
        val === 0,
      then: yup.object().required().nullable(),
      otherwise: yup.object().notRequired().nullable(),
    }),
    [titleIdKey]: yup.number().when(nonAasKey, {
      is: (item) => item && typeof item.value === 'number' && item.value === 0,
      then: yup.number().required().typeError(defaultMessage),
      otherwise: yup
        .number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .nullable(),
    }),
    [nameKey]: conditionalNameValidation('Referee name', nonAasKey),
    [lastNameKey]: conditionalNameValidation('Referee last name', nonAasKey),
    [emailKey]: yup.string().when(nonAasKey, {
      is: (item) => item && typeof item.value === 'number' && item.value === 0,
      then: yup.string().transform(trimString).max(254).email().required(),
      otherwise: yup.string().notRequired().nullable(),
    }),
    [countryIdKey]: yup.object().when(nonAasKey, {
      is: (item) => item && typeof item.value === 'number' && item.value === 0,
      then: yup.object().required(),
      otherwise: yup.object().notRequired().nullable(),
    }),
    [orgKey]: yup.string().when([nonAasKey, emailEduKey], {
      is: (nonAasRefereeId, emailEdu) =>
        nonAasRefereeId &&
        typeof nonAasRefereeId.value === 'number' &&
        nonAasRefereeId.value === 0 &&
        !emailEdu,
      then: yup.string().transform(trimString).required(),
      otherwise: yup.string().notRequired().nullable(),
    }),
    [membershipsKey]: yup.string().when(nonAasKey, {
      is: (item) => item && typeof item.value === 'number' && item.value === 0,
      then: yup.string().transform(trimString).max(1500).required(),
      otherwise: yup.string().notRequired().nullable(),
    }),
    [suppliedMembershipsKey]: yup.mixed().when([nonAasKey, membershipsKey], {
      is: (nonAasRefereeId, refereeMemberships) => {
        const hasReferee = !!(
          nonAasRefereeId &&
          typeof nonAasRefereeId.value === 'number' &&
          nonAasRefereeId.value > 0
        );
        const noRegularMemberships =
          refereeMemberships === undefined ||
          (typeof refereeMemberships === 'string' &&
            refereeMemberships.trim().length === 0);
        return hasReferee && noRegularMemberships;
      },
      then: yup.lazy((value) => {
        if (value === undefined) {
          return yup.mixed().nullable();
        }
        return yup
          .string()
          .transform(trimString)
          .max(1500)
          .required('Academy memberships are required');
      }),
      otherwise: yup.mixed().transform(trimString).nullable(),
    }),
  };
};

const schema = yup.object().shape({
  // STEP ONE
  electionTypeId: yup.number().required().typeError(defaultMessage),
  titleId: yup.number().required().typeError(defaultMessage),

  name: yup
    .string()
    .transform(trimString)
    .min(2)
    .matches(
      /^[\p{L}\s'.-]+$/u,
      'Name can only contain letters, spaces, hyphens, apostrophes, and periods',
    )
    .required()
    .test(
      'expired-nomination',
      'Candidate has a recently expired nomination.',
      validateExpiredNomination,
    )
    .test(
      'already-elected',
      'Candidate has already been elected.',
      validateAlreadyElected,
    )
    .test(
      'deceased-candidate',
      'Candidate is deceased and cannot be nominated.',
      validateDeceasedCandidate,
    )
    .test(
      'active-nomination-duplicate',
      'This candidate already has an active nomination by another proposer.',
      validateActiveNominationDuplicate,
    )
    .test(
      'draft-duplicate',
      'Another proposer is already preparing a nomination for this candidate.',
      validateDraftDuplicate,
    ),

  lastName: yup
    .string()
    .transform(trimString)
    .min(2)
    .matches(
      /^[\p{L}\s'.-]+$/u,
      'Last name can only contain letters, spaces, hyphens, apostrophes, and periods',
    )
    .required()
    .test(
      'expired-nomination',
      'Candidate has a recently expired nomination.',
      validateExpiredNomination,
    )
    .test(
      'already-elected',
      'Candidate has already been elected.',
      validateAlreadyElected,
    )
    .test(
      'deceased-candidate',
      'Candidate is deceased and cannot be nominated.',
      validateDeceasedCandidate,
    )
    .test(
      'active-nomination-duplicate',
      'This candidate already has an active nomination by another proposer.',
      validateActiveNominationDuplicate,
    )
    .test(
      'draft-duplicate',
      'Another proposer is already preparing a nomination for this candidate.',
      validateDraftDuplicate,
    ),

  phone: yup
    .string()
    .transform((value) =>
      typeof value === 'string' ? value.replace(/\s/g, '') : value,
    )
    .max(20, 'Phone number cannot exceed 20 digits')
    .matches(
      /^\+?[0-9]{8,}$/,
      'Must be at least 8 digits and only valid phone numbers',
    )
    .required('Phone number is required'),
  address: yup.string().transform(trimString).min(10).required(),

  email: yup
    .string()
    .email()
    .required()
    .test(
      'expired-nomination',
      'Candidate has a recently expired nomination.',
      validateExpiredNomination,
    )
    .test(
      'already-elected',
      'Candidate has already been elected.',
      validateAlreadyElected,
    )
    .test(
      'deceased-candidate',
      'Candidate is deceased and cannot be nominated.',
      validateDeceasedCandidate,
    )
    .test(
      'active-nomination-duplicate',
      'This candidate already has an active nomination by another proposer.',
      validateActiveNominationDuplicate,
    )
    .test(
      'draft-duplicate',
      'Another proposer is already preparing a nomination for this candidate.',
      validateDraftDuplicate,
    ),

  stateTerritoryId: yup.number().when('electionTypeId', {
    is: (value) =>
      value && typeof value === 'number' && (value === 2 || value === 3),
    then: yup.number().required().nullable().typeError(defaultMessage),
    otherwise: yup
      .number()
      .notRequired()
      .transform((value) => (Number.isNaN(value) ? undefined : value))
      .nullable(),
  }),

  genderId: yup.number().required().typeError(defaultMessage),
  otherGender: yup.string().when('genderId', {
    is: (value) => value && typeof value === 'number' && value === 1,
    then: yup
      .string()
      .transform(trimString)
      .min(2)
      .required()
      .nullable()
      .typeError(defaultMessage),
    otherwise: yup.string().transform(trimString).notRequired().nullable(),
  }),
  dob: yup.date().required().typeError(defaultMessage),

  countryOfBirthId: yup.object().required().nullable(),

  australianStatusId: yup.number().when('electionTypeId', {
    is: (val) => val === 2 || val === 3,
    then: yup.number().required().typeError(defaultMessage),
    otherwise: yup.number().notRequired().nullable().typeError(defaultMessage),
  }),
  arrivalDate: yup.date().when('australianStatusId', {
    is: 3,
    then: yup.date().required().typeError(defaultMessage),
    otherwise: yup.date().notRequired(),
  }),
  nationalityId: yup.array().when('electionTypeId', {
    is: 1,
    then: yup
      .array()
      .min(1, 'At least one nationality is required')
      .required()
      .typeError(defaultMessage),
    otherwise: yup.array().notRequired().nullable(),
  }),

  postNominal: yup.string().transform(trimString).required(),

  jobTitle: yup.string().transform(trimString).required(),
  organisationId: yup.object().required().nullable(),
  otherOrganisation: yup.string().when('organisationId', {
    is: (value) => value && typeof value === 'object' && value.value === 0,
    then: yup
      .string()
      .transform(trimString)
      .min(2)
      .required()
      .nullable()
      .typeError(defaultMessage),
    otherwise: yup.string().transform(trimString).notRequired().nullable(),
  }),

  phdAwardedYear: yup
    .number()
    .integer()
    .when('noPhd', {
      is: (noPhd) => noPhd === undefined || noPhd === false,
      then: yup
        .number()
        .integer()
        .min(minimumYear, `Year must be greater than ${minimumYear}`)
        .max(
          phdAwardedYearMaxValue,
          `Year must be less than ${phdAwardedYearMaxValue}`,
        )
        .test(
          'phd-after-birth',
          'PhD awarded year cannot be before or equal to date of birth',
          function (value) {
            const { dob } = this.parent;
            if (!value || !dob) return true;

            const birthYear = new Date(dob).getFullYear();
            return value > birthYear;
          },
        )
        .required()
        .typeError('Must be a valid year'),
      otherwise: yup
        .number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .nullable(),
    }),

  diverseBackground: yup.bool().required().typeError(defaultMessage),

  nominationConsent: yup.mixed().when('nominationConsentFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),
  retractedPapers: yup.bool(),
  correctedPapers: yup.bool(),
  publicationConcerns: yup.bool(),

  disclosureDeclaration: yup.mixed().when('disclosureDeclarationFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),

  // STEP TWO
  careerInterruption: yup
    .bool()
    .transform((value) => {
      if (value === '' || value === null || value === undefined)
        return undefined;
      if (value === 'true') return true;
      if (value === 'false') return false;
      return value;
    })
    .required(),
  scientificExcellence: yup.string().transform(trimString).required(),

  // STEP THREE
  citation: yup.string().transform(trimString).min(20).required(),

  // STEP FOUR
  sectionalCommitteeId: yup
    .mixed()
    .transform((value) => (value === 'none' ? null : Number(value))) // Transform "none" to null and cast other values to numbers
    .when('electionTypeId', {
      is: 2,
      then: yup.number().min(1).required().typeError(defaultMessage),
      otherwise: yup.number().notRequired().nullable(),
    }),

  disciplineDescriptor: yup
    .array()
    .when(['electionTypeId', 'sectionalCommitteeId'], {
      is: (electionTypeId, sectionalCommitteeId) =>
        electionTypeId !== ELECTION_TYPE.ORDINARY ||
        sectionalCommitteeId === 13 ||
        sectionalCommitteeId === null,
      then: yup.array().notRequired().nullable(),
      otherwise: yup.array().min(1, descriptorErrorMessage).required(),
    }),

  overlapCommittee: yup
    .mixed()
    .when(['electionTypeId', 'sectionalCommitteeId'], {
      is: (electionTypeId, sectionalCommitteeId) =>
        electionTypeId !== ELECTION_TYPE.ORDINARY ||
        sectionalCommitteeId === 13 ||
        sectionalCommitteeId === null,
      then: yup.string().notRequired(),
      otherwise: yup.bool().required().typeError(defaultMessage),
    }),

  overlapSectionalCommitteeId: yup.number().when('overlapCommittee', {
    is: true,
    then: yup.number().min(1).required().typeError(defaultMessage),
    otherwise: yup
      .number()
      .transform((value) => (Number.isNaN(value) ? undefined : value))
      .nullable(),
  }),
  overlapDisciplineDescriptor: yup.array().when('overlapCommittee', {
    is: true,
    then: yup.array().min(1, descriptorErrorMessage).required(),
    otherwise: yup.array().notRequired().nullable(),
  }),
  interdisciplinaryCommittee: yup.array().when('sectionalCommitteeId', {
    is: 13,
    then: yup.array().min(2, interdisciplinaryErrorMessage).required(),
    otherwise: yup.array().notRequired().nullable(),
  }),

  // STEP FIVE
  proposerFellowId: yup.object().required().nullable(),
  proposerSignature: yup.mixed().when('proposerSignatureFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),

  seconderFellowId: yup.object().required().nullable(),
  seconderSignature: yup.mixed().when('seconderSignatureFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),

  supporterOneTypeId: yup.number().required().typeError(defaultMessage),
  supporterOneFellowId: yup.object().required().nullable(),
  supporterOneSignature: yup.mixed().when('supporterOneSignatureFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),

  supporterTwoTypeId: yup.number().required().typeError(defaultMessage),
  supporterTwoFellowId: yup.object().required().nullable(),
  supporterTwoSignature: yup.mixed().when('supporterTwoSignatureFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),

  supporterThreeTypeId: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .nullable(),
  supporterThreeFellowId: yup.object().when('supporterThreeTypeId', {
    is: (val) => val && typeof val === 'number',
    then: yup.object().required().nullable(),
    otherwise: yup.object().notRequired().nullable(),
  }),

  supporterThreeSignature: yup
    .mixed()
    .when(['supporterThreeTypeId', 'supporterThreeSignatureFileId'], {
      is: (supporterThreeTypeId, supporterThreeSignatureFileId) =>
        supporterThreeTypeId === undefined ||
        (supporterThreeSignatureFileId &&
          typeof supporterThreeSignatureFileId === 'object' &&
          typeof supporterThreeSignatureFileId.id === 'number'),
      then: yup.mixed().notRequired().nullable(),
      otherwise: yup
        .mixed()
        .test('is-required', defaultMessage, validateFileRequired)
        .test('is-valid-type', notAPdfFile, validatePdfType)
        .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
    })
    .when(['supporterThreeTypeId', 'supporterThreeSignatureFileId'], {
      is: (supporterThreeTypeId, supporterThreeSignatureFileId) =>
        supporterThreeTypeId &&
        typeof supporterThreeTypeId === 'number' &&
        supporterThreeSignatureFileId === undefined,
      then: yup
        .mixed()
        .test('is-required', defaultMessage, validateFileRequired)
        .test('fileSize', pdfFileInputMessage, validatePdfSize),
      otherwise: yup.mixed().notRequired(),
    }),

  supporterFourTypeId: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .nullable(),
  supporterFourFellowId: yup.object().when('supporterFourTypeId', {
    is: (val) => val && typeof val === 'number',
    then: yup.object().required().nullable(),
    otherwise: yup.object().notRequired().nullable(),
  }),
  supporterFourSignature: yup
    .mixed()
    .when(['supporterFourTypeId', 'supporterFourSignatureFileId'], {
      is: (supporterFourTypeId, supporterFourSignatureFileId) =>
        supporterFourTypeId === undefined ||
        (supporterFourSignatureFileId &&
          typeof supporterFourSignatureFileId === 'object' &&
          typeof supporterFourSignatureFileId.id === 'number'),
      then: yup.mixed().notRequired().nullable(),
      otherwise: yup
        .mixed()
        .test('is-required', defaultMessage, validateFileRequired)
        .test('is-valid-type', notAPdfFile, validatePdfType)
        .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
    })
    .when(['supporterFourTypeId', 'supporterFourSignatureFileId'], {
      is: (supporterFourTypeId, supporterFourSignatureFileId) =>
        supporterFourTypeId &&
        typeof supporterFourTypeId === 'number' &&
        supporterFourSignatureFileId === undefined,
      then: yup
        .mixed()
        .test('is-required', defaultMessage, validateFileRequired)
        .test('fileSize', pdfFileInputMessage, validatePdfSize),
      otherwise: yup.mixed().notRequired(),
    }),

  meetsConduct: yup.bool().required().typeError(defaultMessage),

  // STEP SIX
  extendedCitation: yup.mixed().when('extendedCitationFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),

  // STEP SEVEN
  curriculumVitae: yup.mixed().when('curriculumVitaeFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),

  // STEP EIGHT
  significantPublications: yup.mixed().when('significantPublicationsFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),

  // STEP NINE
  publications: yup.mixed().when('publicationsFileId', {
    is: (val) => val && typeof val === 'object' && typeof val.id === 'number',
    then: yup.object().notRequired().nullable(),
    otherwise: yup
      .mixed()
      .test('is-required', defaultMessage, validateFileRequired)
      .test('is-valid-type', notAPdfFile, validatePdfType)
      .test('is-valid-size', pdfFileInputMessage, validatePdfSize),
  }),

  // STEP TEN
  ...buildRefereeFields('One'),
  ...buildRefereeFields('Two'),
  ...buildRefereeFields('Three'),
  ...buildRefereeFields('Four'),

  refereeFive: yup
    .mixed()
    .transform((value) => {
      if (
        value === 'none' ||
        value === '' ||
        value === null ||
        value === undefined
      ) {
        return undefined;
      }
      return Number(value);
    })
    .when('electionTypeId', {
      is: (val) => val && typeof val === 'number' && val === 2,
      then: yup.number().required().typeError(defaultMessage),
      otherwise: yup.mixed().nullable(),
    }),

  refereeFiveFellowId: yup.object().when(['refereeFive', 'electionTypeId'], {
    is: (refereeFive, electionTypeId) =>
      refereeFive &&
      typeof refereeFive === 'number' &&
      refereeFive === 1 &&
      electionTypeId &&
      typeof electionTypeId === 'number' &&
      electionTypeId === ELECTION_TYPE.ORDINARY,
    then: yup.object().required().nullable(),
    otherwise: yup.object().notRequired().nullable(),
  }),
  nonAasRefereeFiveId: yup.object().when(['refereeFive', 'electionTypeId'], {
    is: (refereeFive, electionTypeId) =>
      refereeFive !== null &&
      typeof refereeFive !== 'undefined' &&
      typeof refereeFive === 'number' &&
      refereeFive === 0 &&
      electionTypeId &&
      typeof electionTypeId === 'number' &&
      electionTypeId === ELECTION_TYPE.ORDINARY,
    then: yup.object().required().nullable(),
    otherwise: yup.object().notRequired().nullable(),
  }),
  refereeFiveTitleId: yup
    .number()
    .when(['electionTypeId', 'nonAasRefereeFiveId'], {
      is: (electionTypeId, nonAasRefereeFiveId) =>
        electionTypeId === ELECTION_TYPE.ORDINARY &&
        nonAasRefereeFiveId &&
        typeof nonAasRefereeFiveId.value === 'number' &&
        nonAasRefereeFiveId.value === 0,
      then: yup.number().required().typeError(defaultMessage),
      otherwise: yup
        .number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .nullable(),
    }),
  refereeFiveName: conditionalNameValidationWithElectionType(
    'Referee name',
    'nonAasRefereeFiveId',
  ),
  refereeFiveLastName: conditionalNameValidationWithElectionType(
    'Referee last name',
    'nonAasRefereeFiveId',
  ),
  refereeFiveEmail: yup
    .string()
    .when(['electionTypeId', 'nonAasRefereeFiveId'], {
      is: (electionTypeId, nonAasRefereeFiveId) =>
        electionTypeId === ELECTION_TYPE.ORDINARY &&
        nonAasRefereeFiveId &&
        typeof nonAasRefereeFiveId.value === 'number' &&
        nonAasRefereeFiveId.value === 0,
      then: yup.string().transform(trimString).max(254).email().required(),
      otherwise: yup.string().notRequired().nullable(),
    }),
  refereeFiveCountryId: yup
    .object()
    .when(['electionTypeId', 'nonAasRefereeFiveId'], {
      is: (electionTypeId, nonAasRefereeFiveId) =>
        electionTypeId === ELECTION_TYPE.ORDINARY &&
        nonAasRefereeFiveId &&
        typeof nonAasRefereeFiveId.value === 'number' &&
        nonAasRefereeFiveId.value === 0,
      then: yup.object().required(),
      otherwise: yup.object().notRequired().nullable(),
    }),
  refereeFiveOrganisation: yup
    .string()
    .when(
      [
        'electionTypeId',
        'nonAasRefereeFiveId',
        'refereeFiveEmailEducationalInstitution',
      ],
      {
        is: (
          electionTypeId,
          nonAasRefereeFiveId,
          refereeFiveEmailEducationalInstitution,
        ) =>
          electionTypeId === ELECTION_TYPE.ORDINARY &&
          nonAasRefereeFiveId &&
          typeof nonAasRefereeFiveId.value === 'number' &&
          nonAasRefereeFiveId.value === 0 &&
          !refereeFiveEmailEducationalInstitution,
        then: yup.string().transform(trimString).required(),
        otherwise: yup.string().notRequired().nullable(),
      },
    ),
  refereeFiveMemberships: yup
    .string()
    .when(['electionTypeId', 'nonAasRefereeFiveId'], {
      is: (electionTypeId, nonAasRefereeFiveId) =>
        electionTypeId === ELECTION_TYPE.ORDINARY &&
        nonAasRefereeFiveId &&
        typeof nonAasRefereeFiveId.value === 'number' &&
        nonAasRefereeFiveId.value === 0,
      then: yup.string().transform(trimString).max(1500).required(),
      otherwise: yup.string().notRequired().nullable(),
    }),
  refereeFiveSuppliedMemberships: yup
    .mixed()
    .when(['nonAasRefereeFiveId', 'refereeFiveMemberships'], {
      is: (nonAasRefereeFiveId, refereeFiveMemberships) => {
        const hasReferee = !!(
          nonAasRefereeFiveId &&
          typeof nonAasRefereeFiveId.value === 'number' &&
          nonAasRefereeFiveId.value > 0
        );

        const noRegularMemberships =
          refereeFiveMemberships === undefined ||
          (typeof refereeFiveMemberships === 'string' &&
            refereeFiveMemberships.trim().length === 0);

        return hasReferee && noRegularMemberships;
      },
      then: yup.lazy((value) => {
        if (value === undefined) {
          return yup.mixed().nullable();
        }

        return yup
          .string()
          .transform(trimString)
          .max(1500)
          .required('Academy memberships are required');
      }),
      otherwise: yup.mixed().transform(trimString).nullable(),
    }),

  refereeSix: yup
    .mixed()
    .transform((value) => {
      if (
        value === 'none' ||
        value === '' ||
        value === null ||
        value === undefined
      ) {
        return undefined;
      }
      return Number(value);
    })
    .when('electionTypeId', {
      is: (val) => val && typeof val === 'number' && val === 2,
      then: yup.number().required().typeError(defaultMessage),
      otherwise: yup.mixed().nullable(),
    }),

  refereeSixFellowId: yup.object().when(['refereeSix', 'electionTypeId'], {
    is: (refereeSix, electionTypeId) =>
      refereeSix &&
      typeof refereeSix === 'number' &&
      refereeSix === 1 &&
      electionTypeId &&
      typeof electionTypeId === 'number' &&
      electionTypeId === ELECTION_TYPE.ORDINARY,
    then: yup.object().required().nullable(),
    otherwise: yup.object().notRequired().nullable(),
  }),
  nonAasRefereeSixId: yup.object().when(['refereeSix', 'electionTypeId'], {
    is: (refereeSix, electionTypeId) =>
      refereeSix !== null &&
      typeof refereeSix !== 'undefined' &&
      typeof refereeSix === 'number' &&
      refereeSix === 0 &&
      electionTypeId &&
      typeof electionTypeId === 'number' &&
      electionTypeId === ELECTION_TYPE.ORDINARY,
    then: yup.object().required().nullable(),
    otherwise: yup.object().notRequired().nullable(),
  }),
  refereeSixTitleId: yup
    .number()
    .when(['electionTypeId', 'nonAasRefereeSixId'], {
      is: (electionTypeId, nonAasRefereeSixId) =>
        electionTypeId === ELECTION_TYPE.ORDINARY &&
        nonAasRefereeSixId &&
        typeof nonAasRefereeSixId.value === 'number' &&
        nonAasRefereeSixId.value === 0,
      then: yup.number().required().typeError(defaultMessage),
      otherwise: yup
        .number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .nullable(),
    }),
  refereeSixName: conditionalNameValidationWithElectionType(
    'Referee name',
    'nonAasRefereeSixId',
  ),
  refereeSixLastName: conditionalNameValidationWithElectionType(
    'Referee last name',
    'nonAasRefereeSixId',
  ),
  refereeSixEmail: yup.string().when(['electionTypeId', 'nonAasRefereeSixId'], {
    is: (electionTypeId, nonAasRefereeSixId) =>
      electionTypeId === ELECTION_TYPE.ORDINARY &&
      nonAasRefereeSixId &&
      typeof nonAasRefereeSixId.value === 'number' &&
      nonAasRefereeSixId.value === 0,
    then: yup.string().transform(trimString).max(254).email().required(),
    otherwise: yup.string().notRequired().nullable(),
  }),
  refereeSixCountryId: yup
    .object()
    .when(['electionTypeId', 'nonAasRefereeSixId'], {
      is: (electionTypeId, nonAasRefereeSixId) =>
        electionTypeId === ELECTION_TYPE.ORDINARY &&
        nonAasRefereeSixId &&
        typeof nonAasRefereeSixId.value === 'number' &&
        nonAasRefereeSixId.value === 0,
      then: yup.object().required(),
      otherwise: yup.object().notRequired().nullable(),
    }),
  refereeSixOrganisation: yup
    .string()
    .when(
      [
        'electionTypeId',
        'nonAasRefereeSixId',
        'refereeSixEmailEducationalInstitution',
      ],
      {
        is: (
          electionTypeId,
          nonAasRefereeSixId,
          refereeSixEmailEducationalInstitution,
        ) =>
          electionTypeId === ELECTION_TYPE.ORDINARY &&
          nonAasRefereeSixId &&
          typeof nonAasRefereeSixId.value === 'number' &&
          nonAasRefereeSixId.value === 0 &&
          !refereeSixEmailEducationalInstitution,
        then: yup.string().transform(trimString).required().nullable(),
        otherwise: yup.string().notRequired().nullable(),
      },
    ),
  refereeSixMemberships: yup
    .string()
    .when(['electionTypeId', 'nonAasRefereeSixId'], {
      is: (electionTypeId, nonAasRefereeSixId) =>
        electionTypeId === ELECTION_TYPE.ORDINARY &&
        nonAasRefereeSixId &&
        typeof nonAasRefereeSixId.value === 'number' &&
        nonAasRefereeSixId.value === 0,
      then: yup.string().transform(trimString).max(1500).required(),
      otherwise: yup.string().notRequired().nullable(),
    }),
  refereeSixSuppliedMemberships: yup
    .mixed()
    .when(['nonAasRefereeSixId', 'refereeSixMemberships'], {
      is: (nonAasRefereeSixId, refereeSixMemberships) => {
        const hasReferee = !!(
          nonAasRefereeSixId &&
          typeof nonAasRefereeSixId.value === 'number' &&
          nonAasRefereeSixId.value > 0
        );

        const noRegularMemberships =
          refereeSixMemberships === undefined ||
          (typeof refereeSixMemberships === 'string' &&
            refereeSixMemberships.trim().length === 0);

        return hasReferee && noRegularMemberships;
      },
      then: yup.lazy((value) => {
        if (value === undefined) {
          return yup.mixed().nullable();
        }

        return yup
          .string()
          .transform(trimString)
          .max(1500)
          .required('Academy memberships are required');
      }),
      otherwise: yup.mixed().transform(trimString).nullable(),
    }),
});

export default schema;
